/**
 * canvasTelemetry — fire-and-forget UX telemetry for the Canvas Task Picker.
 *
 * Spec: agentlets-production/CANVAS_PICKER_INLINE_DESIGN.md §10 (acceptance
 * criteria for "loved" — driven by these events).
 *
 * Why this exists:
 *   • We declared §10 success metrics (≥60% of insertions through picker,
 *     ≥30% through Recommended/Recipes/Recent, etc.). Those need event data.
 *   • Existing audit_log endpoints are tied to workflow runs, not canvas
 *     interactions. A separate channel keeps backend audit clean.
 *
 * Posture:
 *   • FAIL-SOFT. The endpoint may not exist yet; we swallow 404 / network
 *     errors silently. UX must never depend on telemetry succeeding.
 *   • BATCHED. Events queue locally and flush on a timer (5s) or when the
 *     queue hits 50 items. Reduces request count by ~50× during heavy use.
 *   • PRIVACY-RESPECTING. Only event metadata travels — never node config,
 *     never user text, never connector secrets. Workflow id is the only
 *     identifying field, and only when the user is signed in to that
 *     workflow.
 *   • OFFLINE-TOLERANT. If the network is down, events stay in-memory and
 *     drop after the 50-item cap (no localStorage spam).
 *
 * Wire-shape (request body):
 *   POST /api/v1/audit/canvas-events
 *   { events: [{ name, ts, sessionId, workflowId?, data? }, ...] }
 *
 *   `name` is one of:
 *     picker_open      — the picker was shown
 *     picker_close     — closed without insertion
 *     row_chosen       — a row was inserted (data.kind = 'type'|'recipe'|...)
 *     recipe_used      — convenience alias of row_chosen with kind='recipe'
 *     dragout_used     — drag-out escape hatch was used (Phase 6 stretch)
 *     hint_followed    — a Recommended-here row was the chosen one
 *     search_used      — a search query was issued (data.queryLength only)
 *
 * The backend can land later; the frontend already queues correctly.
 */

const ENDPOINT = '/api/v1/audit/canvas-events';
const FLUSH_INTERVAL_MS = 5000;
const QUEUE_CAP = 50;

let queue = [];
let flushTimer = null;
let sessionId = null;
let endpointKnownMissing = false;

/** Generate a session id once per page load. Lets us correlate events without
 *  needing a backend cookie or a globally-issued id. */
function ensureSessionId() {
  if (sessionId) return sessionId;
  // crypto.randomUUID is widely supported in modern browsers; fall back to
  // a simple random hex for old Safari / test environments without it.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    sessionId = crypto.randomUUID();
  } else {
    sessionId = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
  return sessionId;
}

function scheduleFlush() {
  if (flushTimer) return;
  if (typeof window === 'undefined') return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

async function flush() {
  if (endpointKnownMissing) return;
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Authoritative bearer token comes from the existing fetch wrapper
      // when this lands behind it; keep this layer raw to avoid coupling
      // to the auth module — the wrapper can be added later without
      // touching this file.
      credentials: 'include',
      body: JSON.stringify({ events: batch }),
      // Keep the request hint-light so it doesn't show up in DevTools as
      // "long-running". 5s is plenty for a tiny POST.
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout
        ? AbortSignal.timeout(5000)
        : undefined,
    });
    if (res.status === 404 || res.status === 405) {
      // Endpoint isn't deployed yet — stop trying for the rest of this
      // session so we don't churn the network.
      endpointKnownMissing = true;
    }
    // Other non-2xx: drop the batch. Telemetry is best-effort.
  } catch (_err) {
    // Network down / aborted / CORS — also best-effort; the batch is gone.
  }
}

/**
 * Public API. Pushes one event into the queue and schedules a flush.
 * Never throws.
 *
 * @param {string} name        — one of the event names above
 * @param {object} [data]      — event-specific metadata
 * @param {object} [meta]      — { workflowId? }
 */
export function track(name, data, meta) {
  if (!name) return;
  if (queue.length >= QUEUE_CAP) {
    // Drop oldest to keep memory bounded.
    queue.shift();
  }
  queue.push({
    name,
    ts: new Date().toISOString(),
    sessionId: ensureSessionId(),
    workflowId: meta?.workflowId || null,
    data: data || null,
  });
  if (queue.length >= 10) flush();
  else scheduleFlush();
}

/** Best-effort flush on page hide. Browsers sometimes drop in-flight requests
 *  on unload; sendBeacon is the right primitive but is not always available. */
if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (queue.length === 0 || endpointKnownMissing) return;
    try {
      const blob = new Blob(
        [JSON.stringify({ events: queue })],
        { type: 'application/json' }
      );
      if (typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(ENDPOINT, blob);
        queue = [];
      }
    } catch (_) { /* swallow — best-effort */ }
  });
}

/** Test-only: resets internal state. Not part of the production API. */
export function _resetForTest() {
  queue = [];
  flushTimer && clearTimeout(flushTimer);
  flushTimer = null;
  sessionId = null;
  endpointKnownMissing = false;
}
